import { ref, computed, onMounted, onUnmounted, type Ref } from 'vue'

export interface ParallaxConfig {
  card: {
    rotateYFactor: number
    rotateXFactor: number
    translateY: number
  }
  background: {
    parallaxXFactor: number
    parallaxYFactor: number
    scale: number
  }
  case: {
    parallaxXFactor: number
    parallaxYFactor: number
    scale: number
  }
  weapon: {
    parallaxXFactor: number
    parallaxYFactor: number
    rotateFactor: number
    scale: number
  }
}

const DEFAULT_CONFIG: ParallaxConfig = {
  card: {
    rotateYFactor: -2.5,
    rotateXFactor: 3.75,
    translateY: -2,
  },
  background: {
    parallaxXFactor: -0.75,
    parallaxYFactor: -1,
    scale: 1.0025,
  },
  case: {
    parallaxXFactor: -1.125,
    parallaxYFactor: -1.5,
    scale: 1.00125,
  },
  weapon: {
    parallaxXFactor: -1.875,
    parallaxYFactor: -2.5,
    rotateFactor: -0.25,
    scale: 1.005,
  },
}

export function useCardParallax(cardRef: Ref<HTMLElement | undefined>, config: Partial<ParallaxConfig> = {}) {
  const mergedConfig = { ...DEFAULT_CONFIG, ...config }

  const mouseX = ref(0)
  const mouseY = ref(0)
  const isHovering = ref(false)
  const isInteractive = ref(false)
  const isVisible = ref(true)

  let cachedRect: { centerX: number; centerY: number; halfWidth: number; halfHeight: number } | null = null
  let rafId: number | null = null
  let observer: IntersectionObserver | null = null

  // Computed styles for card
  const cardStyle = computed(() => {
    if (!isHovering.value) {
      return {
        transform: 'perspective(1000px) rotateX(0deg) rotateY(0deg) translateY(0px)',
        transition: 'transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      }
    }

    const rotateY = (mouseX.value - 0.5) * mergedConfig.card.rotateYFactor
    const rotateX = (mouseY.value - 0.5) * mergedConfig.card.rotateXFactor
    const translateY = mergedConfig.card.translateY

    return {
      transform: `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateY(${translateY}px)`,
      transition: 'transform 0.1s ease-out',
    }
  })

  // Computed styles for background layer
  const backgroundStyle = computed(() => {
    if (!isHovering.value) return {}

    const parallaxX = (mouseX.value - 0.5) * mergedConfig.background.parallaxXFactor
    const parallaxY = (mouseY.value - 0.5) * mergedConfig.background.parallaxYFactor
    const scale = mergedConfig.background.scale

    return {
      transform: `translateZ(-20px) translateX(${parallaxX}px) translateY(${parallaxY}px) scale(${scale})`,
    }
  })

  // Computed styles for case layer
  const caseStyle = computed(() => {
    if (!isHovering.value) return {}

    const parallaxX = (mouseX.value - 0.5) * mergedConfig.case.parallaxXFactor
    const parallaxY = (mouseY.value - 0.5) * mergedConfig.case.parallaxYFactor
    const scale = mergedConfig.case.scale

    return {
      transform: `translateZ(0px) translateX(${parallaxX}px) translateY(${parallaxY}px) scale(${scale})`,
    }
  })

  // Computed styles for weapon layer
  const weaponStyle = computed(() => {
    if (!isHovering.value) return {}

    const parallaxX = (mouseX.value - 0.5) * mergedConfig.weapon.parallaxXFactor
    const parallaxY = (mouseY.value - 0.5) * mergedConfig.weapon.parallaxYFactor
    const rotateZ = (mouseX.value - 0.5) * mergedConfig.weapon.rotateFactor
    const scale = mergedConfig.weapon.scale

    return {
      transform: `translateZ(20px) translateX(${parallaxX}px) translateY(${parallaxY}px) scale(${scale}) rotateZ(${rotateZ}deg)`,
    }
  })

  // Throttled mousemove handler using requestAnimationFrame
  function handleMouseMove(event: MouseEvent) {
    if (!isInteractive.value || !isVisible.value || !cachedRect) return

    if (rafId !== null) {
      cancelAnimationFrame(rafId)
    }

    rafId = requestAnimationFrame(() => {
      const { centerX, centerY, halfWidth, halfHeight } = cachedRect!

      // Normalize to -1 to 1 range
      mouseX.value = Math.max(-1, Math.min(1, (event.clientX - centerX) / halfWidth))
      mouseY.value = Math.max(-1, Math.min(1, (event.clientY - centerY) / halfHeight))

      isHovering.value = true
      rafId = null
    })
  }

  function handleMouseEnter() {
    if (!isVisible.value || !cardRef.value) return

    // Cache getBoundingClientRect result on hover
    const rect = cardRef.value.getBoundingClientRect()
    cachedRect = {
      centerX: rect.left + rect.width / 2,
      centerY: rect.top + rect.height / 2,
      halfWidth: rect.width / 2,
      halfHeight: rect.height / 2,
    }

    isInteractive.value = true
  }

  function handleMouseLeave() {
    isHovering.value = false
    isInteractive.value = false
    mouseX.value = 0
    mouseY.value = 0
    cachedRect = null

    if (rafId !== null) {
      cancelAnimationFrame(rafId)
      rafId = null
    }
  }

  // Setup intersection observer on mount
  onMounted(() => {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window) || !cardRef.value) {
      return
    }

    observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries
        if (entry) {
          isVisible.value = entry.isIntersecting
          if (!entry.isIntersecting && isInteractive.value) {
            handleMouseLeave()
          }
        }
      },
      {
        rootMargin: '100px',
        threshold: 0,
      },
    )

    observer.observe(cardRef.value)
  })

  // Cleanup on unmount
  onUnmounted(() => {
    if (rafId !== null) {
      cancelAnimationFrame(rafId)
    }
    observer?.disconnect()
  })

  return {
    // State
    isHovering,
    isInteractive,
    isVisible,

    // Computed styles
    cardStyle,
    backgroundStyle,
    caseStyle,
    weaponStyle,

    // Event handlers
    handleMouseMove,
    handleMouseEnter,
    handleMouseLeave,
  }
}
